import React, { useState } from "react";
import { Box, Container, Grid, Stack, Typography, Link, Button } from "@mui/joy";
import { useNavigate, useParams } from "react-router-dom";
import CompanyHeader from "./CompanyHeader";
import HeroVideo from "./HeroVideo";
import PositionCard from "../../PositionListing/components/PositionCard";
import CallToActionCard from "../../PositionListing/components/CallToActionCard";
import CandidateList from "components/common/CandidateList";
import PositionsListCard from "pages/ApplyForWork/CompanyListing/components/PositionsListCard";
interface ContentLayoutProps {
  employer: {
    companyName: string;
    companyAddress: string;
    introVideoUuid: string;
    about: string;
  };
  employerPosition?: {
    displayNameOverride: string;
    type: string;
    description: string;
    mediaId: string;
  };
  employerPositions?: any[];
  onApplyNow: () => void;
  isEmployerView?: boolean;
  people?: any[];
  peopleLoading?: boolean;
  peopleError?: string | null;
  hasMorePeople?: boolean;
  onLoadMorePeople?: () => Promise<void>;
  totalPeople?: number;
  customFilterHandler?: (filters: { zipcode: string; roles: string[]; employerPositions: string[] }) => void;
  currentEmployerPositions?: string[];
  videoPlaying?: boolean;
  setVideoPlaying?: (playing: boolean) => void;
}

const ContentLayout: React.FC<ContentLayoutProps> = ({ 
  employer, 
  employerPosition,
  employerPositions,
  onApplyNow,
  isEmployerView = false,
  people = [],
  peopleLoading = false,
  peopleError = null,
  hasMorePeople = false,
  onLoadMorePeople,
  totalPeople = 0,
  customFilterHandler,
  currentEmployerPositions = [],
  videoPlaying,
  setVideoPlaying
  }) => {
  const navigate = useNavigate();

  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [internalPlaying, setInternalPlaying] = useState(true);
  const { company, position: positionSlug } = useParams();
  
  // Use external playing state if provided, otherwise use internal state
  const playing = videoPlaying !== undefined ? videoPlaying : internalPlaying;
  const setPlaying = setVideoPlaying !== undefined ? setVideoPlaying : setInternalPlaying;

  const handlePlayerVisibilityChange = (videoId: string, isVisible: boolean) => {
    if (isVisible) {
      setActiveVideoId(videoId);
    }
  };

  const videoId = employerPosition?.mediaId || employer.introVideoUuid;

  return (
    <Box sx={{ flex: 1, pt: 2, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Company Header - Always at top */}
        <CompanyHeader 
          companyName={employer.companyName}
          companyAddress={employer.companyAddress}
        />

        {/* Grid Layout - Video and Content */}
        <Grid container spacing={4}>
          {videoId && (
            <Grid xs={12} md={5}>
              <HeroVideo
                videoId={videoId} 
                isActive={videoId === activeVideoId}
                muted={muted}
                setMuted={setMuted}
                playing={playing}
                setPlaying={setPlaying}
                onVisibilityChange={(isVisible: boolean) => handlePlayerVisibilityChange(videoId, isVisible)}
              />
            </Grid>
          )}

          {/* Content Column */}
          <Grid xs={12} md={videoId ? 7 : 12}>
            <Stack direction="column" spacing={4} sx={{ maxWidth: { xs: "600px", md: "none" }, mx: { xs: "auto", md: videoId ? 0 : "auto" } }}>
              {employerPositions && employerPositions.length > 0 && (
                <PositionsListCard
                  positions={employerPositions}
                  companyName={employer.companyName}
                  companyAbout={employer.about}
                  isEmployerView={isEmployerView}
                />
              )}

              {employerPosition && (
                <PositionCard 
                  position={employerPosition}
                  companyName={employer.companyName}
                  companyAbout={employer.about}
                  onApplyNow={onApplyNow}
                  isEmployerView={isEmployerView}
                />
              )}

              {/* Desktop Call to Action - Hidden on mobile */}
              {!isEmployerView && employerPosition && (
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <CallToActionCard onApplyNow={onApplyNow} />
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Container>
      
    {isEmployerView && (
      <Box sx={{ flex: 1, py: 4 }}>
        <Container maxWidth="lg">
          <Grid xs={12} sx={{ maxWidth: { xs: "600px", md: "none" }, mx: { xs: "auto", md: 0 } }}>
            <CandidateList 
              candidates={people} 
              activeVideoId={activeVideoId || ''}
              muted={muted}
              setMuted={setMuted}
              playing={playing}
              setPlaying={setPlaying}
              onVisibilityChange={handlePlayerVisibilityChange}
              loading={peopleLoading}
              error={peopleError}
              hasMore={hasMorePeople}
              onLoadMore={onLoadMorePeople}
              totalCandidates={totalPeople}
              onShowFilters={() => navigate('/hiring/candidates')}
              enableZipcodeFilter={true}
              enableRolesFilter={false}
              enableEmployerPositionsFilter={false}
              customFilterHandler={customFilterHandler}
              customCurrentEmployerPositions={currentEmployerPositions}
            />
          </Grid>          
        </Container>
      </Box>
    )}

      {/* Mobile Floating Apply Now Button - Only show on mobile for non-employer view with position */}
      {!isEmployerView && employerPosition && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '20px',
            left: 0,
            right: 0,
            zIndex: 1000,
            px: 2,
            display: { xs: 'block', md: 'none' }, // Only show on mobile
          }}
        >
          <Button
            size="lg"
            onClick={onApplyNow}
            sx={{
              bgcolor: "#2E2D7E",
              color: "white",
              px: 6,
              py: 2,
              fontSize: "1.125rem",
              fontWeight: 600,
              borderRadius: "12px",
              width: "100%",
              boxShadow: "0 4px 20px rgba(46, 45, 126, 0.3)",
              "&:hover": {
                bgcolor: "#1a1a5e",
                boxShadow: "0 6px 24px rgba(46, 45, 126, 0.4)",
              },
            }}
          >
            RESPOND NOW
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ContentLayout; 